import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  Grid,
  Divider,
  CircularProgress,
  TextField,
  FormControlLabel,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { getTimeLoggerOrgs, updateTimeLoggerOrg } from "../../services/timeLoggerOrgs.service";
import { ToastError, ToastSuccess } from "../../utils/Toaster/Toaster";
import { usePermissions } from "../../context/PermissionProvider";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// ── Brand palette (matches the sidebar) ─────────────────────────────
const TEAL = "#008080";
const TEAL_GRAD = "linear-gradient(135deg, #008080 0%, #00B8B8 100%)";
const TEAL_GRAD_HOVER = "linear-gradient(135deg, #007373 0%, #00a5a5 100%)";
const TEXT_STRONG = "#0F172A";
const TEXT_MUTED = "#64748B";
const BORDER = "#E2E8F0";

// Teal-focused input styling (overrides MUI's default blue)
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontFamily: "Manrope, sans-serif",
    "& fieldset": { borderColor: BORDER },
    "&:hover fieldset": { borderColor: "#94A3B8" },
    "&.Mui-focused fieldset": { borderColor: TEAL, borderWidth: "1.5px" },
  },
  "& label.Mui-focused": { color: TEAL },
};

const SectionTitle = ({ children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
    <Box sx={{ width: 4, height: 16, borderRadius: 2, background: TEAL_GRAD }} />
    <Typography sx={{ color: TEXT_STRONG, fontWeight: 700, fontSize: 14.5, letterSpacing: 0.2 }}>
      {children}
    </Typography>
  </Box>
);

// Compact numeric field — disabled when user has no edit permission
const Num = ({ label, value, set, step, min, max, sm = 3, disabled }) => (
  <Grid item xs={6} sm={sm}>
    <TextField
      label={label}
      type="number"
      fullWidth
      size="small"
      sx={fieldSx}
      value={value}
      onChange={(e) => set(+e.target.value)}
      inputProps={{ step, min, max }}
      disabled={disabled}
    />
  </Grid>
);

const buildForm = (o) => ({
  name: o.name || "",
  defaultTimezone: o.defaultTimezone || "",
  workingHours: { start: o.workingHours?.start || "09:00", end: o.workingHours?.end || "18:00" },
  workingDays: Array.isArray(o.workingDays) ? [...o.workingDays] : [],
  leaveYearStartMonth: o.leaveYearStartMonth ?? 1,
  idleThresholdMinutes: o.idleThresholdMinutes ?? 5,
  screenshotIntervalMinutes: o.screenshotIntervalMinutes ?? 10,
  untrackedTimeThresholdMinutes: o.untrackedTimeThresholdMinutes ?? 120,
  timesheetAttendanceToleranceMinutes: o.timesheetAttendanceToleranceMinutes ?? 30,
  lateGraceMinutes: o.lateGraceMinutes ?? 15,
  halfDayThresholdHours: o.halfDayThresholdHours ?? 4,
  correctionWindowDays: o.correctionWindowDays ?? 7,
  compOffExpiryDays: o.compOffExpiryDays ?? 0,
  compOffAlertThresholdDays: o.compOffAlertThresholdDays ?? 10,
  compOffRequestWindowDays: o.compOffRequestWindowDays ?? 7,
  managerApprovalEnabled: !!o.managerApprovalEnabled,
  timesheetAutoApprove: !!o.timesheetAutoApprove,
  retention: {
    screenshots: o.retention?.screenshots ?? 90,
    timeEntries: o.retention?.timeEntries ?? 1095,
    attendance: o.retention?.attendance ?? 1095,
    auditLog: o.retention?.auditLog ?? 365,
  },
});

const OrgCard = ({ entry, onSaved, isEditable }) => {
  const { user, org } = entry;
  const [form, setForm] = useState(org ? buildForm(org) : null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(org ? buildForm(org) : null);
  }, [org]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setNested = (parent, key, value) =>
    setForm((f) => ({ ...f, [parent]: { ...f[parent], [key]: value } }));

  const toggleWorkingDay = (d) =>
    setForm((f) => {
      const days = f.workingDays.includes(d)
        ? f.workingDays.filter((x) => x !== d)
        : [...f.workingDays, d].sort((a, b) => a - b);
      return { ...f, workingDays: days };
    });

  const handleSave = async () => {
    if (!org?._id) return;
    setSaving(true);
    try {
      const res = await updateTimeLoggerOrg(org._id, form);
      ToastSuccess(res?.message || "Organisation updated");
      onSaved(res?.data);
    } catch (err) {
      ToastError(err?.message || "Failed to update organisation");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "14px",
        border: `1px solid ${BORDER}`,
        overflow: "hidden",
        mb: 3,
        bgcolor: "#fff",
        boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
      }}
    >
      {/* ── Teal header strip ── */}
      <Box
        sx={{
          background: TEAL_GRAD,
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.18)",
            border: "2px solid rgba(255,255,255,0.6)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 15,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {(org?.name || user.name || user.email || "?").slice(0, 2).toUpperCase()}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 17, lineHeight: 1.2 }} noWrap>
            {org?.name || user.name || user.email}
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: 12.5 }} noWrap>
            {user.name ? `${user.name} · ` : ""}
            {user.email}
          </Typography>
        </Box>
        <Chip
          label={user.isActive ? "Active" : "Inactive"}
          size="small"
          sx={{
            bgcolor: "rgba(255,255,255,0.95)",
            color: user.isActive ? TEAL : "#DC2626",
            fontWeight: 700,
            fontSize: 11,
            height: 24,
          }}
        />
      </Box>

      {/* ── Body ── */}
      <Box sx={{ p: 3 }}>
        {!form ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2, color: TEXT_MUTED }}>
            <BusinessIcon fontSize="small" />
            <Typography variant="body2">No organisation linked to this user.</Typography>
          </Box>
        ) : (
          <>
            {/* General */}
            <SectionTitle>General</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField label="Organisation Name" fullWidth size="small" sx={fieldSx} disabled={!isEditable}
                  value={form.name} onChange={(e) => setField("name", e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Default Timezone" fullWidth size="small" sx={fieldSx} placeholder="e.g. Asia/Kolkata" disabled={!isEditable}
                  value={form.defaultTimezone} onChange={(e) => setField("defaultTimezone", e.target.value)} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField label="Work Start" type="time" fullWidth size="small" sx={fieldSx} InputLabelProps={{ shrink: true }} disabled={!isEditable}
                  value={form.workingHours.start} onChange={(e) => setNested("workingHours", "start", e.target.value)} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField label="Work End" type="time" fullWidth size="small" sx={fieldSx} InputLabelProps={{ shrink: true }} disabled={!isEditable}
                  value={form.workingHours.end} onChange={(e) => setNested("workingHours", "end", e.target.value)} />
              </Grid>
              <Num label="Leave Year Start (1-12)" sm={4} min={1} max={12} disabled={!isEditable}
                value={form.leaveYearStartMonth} set={(v) => setField("leaveYearStartMonth", v)} />
              <Grid item xs={12}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: TEXT_MUTED, mb: 1 }}>
                  Working Days
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  value={form.workingDays}
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    "& .MuiToggleButtonGroup-grouped": {
                      m: 0,
                      border: `1px solid ${BORDER} !important`,
                      borderRadius: "8px !important",
                      textTransform: "none",
                      fontWeight: 600,
                      color: TEXT_MUTED,
                      px: 1.75,
                      "&.Mui-selected": {
                        background: TEAL_GRAD,
                        color: "#fff",
                        "&:hover": { background: TEAL_GRAD_HOVER },
                      },
                    },
                  }}
                >
                  {DAYS.map((d, i) => (
                    <ToggleButton key={i} value={i} selected={form.workingDays.includes(i)}
                      onClick={() => isEditable && toggleWorkingDay(i)} disabled={!isEditable}>
                      {d}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3, borderColor: "#EEF2F6" }} />

            {/* Time Tracking */}
            <SectionTitle>Time Tracking</SectionTitle>
            <Grid container spacing={2}>
              <Num label="Idle Threshold (min)" disabled={!isEditable} value={form.idleThresholdMinutes} set={(v) => setField("idleThresholdMinutes", v)} />
              <Num label="Screenshot Interval (min)" disabled={!isEditable} value={form.screenshotIntervalMinutes} set={(v) => setField("screenshotIntervalMinutes", v)} />
              <Num label="Untracked Threshold (min)" disabled={!isEditable} value={form.untrackedTimeThresholdMinutes} set={(v) => setField("untrackedTimeThresholdMinutes", v)} />
              <Num label="Timesheet Tolerance (min)" disabled={!isEditable} value={form.timesheetAttendanceToleranceMinutes} set={(v) => setField("timesheetAttendanceToleranceMinutes", v)} />
            </Grid>

            <Divider sx={{ my: 3, borderColor: "#EEF2F6" }} />

            {/* Attendance */}
            <SectionTitle>Attendance</SectionTitle>
            <Grid container spacing={2}>
              <Num label="Late Grace (min)" sm={4} disabled={!isEditable} value={form.lateGraceMinutes} set={(v) => setField("lateGraceMinutes", v)} />
              <Num label="Half-Day Threshold (hrs)" sm={4} step={0.5} disabled={!isEditable} value={form.halfDayThresholdHours} set={(v) => setField("halfDayThresholdHours", v)} />
              <Num label="Correction Window (days)" sm={4} disabled={!isEditable} value={form.correctionWindowDays} set={(v) => setField("correctionWindowDays", v)} />
            </Grid>

            <Divider sx={{ my: 3, borderColor: "#EEF2F6" }} />

            {/* Leave */}
            <SectionTitle>Leave</SectionTitle>
            <Grid container spacing={2}>
              <Num label="Comp-off Expiry (0=none)" sm={4} disabled={!isEditable} value={form.compOffExpiryDays} set={(v) => setField("compOffExpiryDays", v)} />
              <Num label="Comp-off Alert (days)" sm={4} disabled={!isEditable} value={form.compOffAlertThresholdDays} set={(v) => setField("compOffAlertThresholdDays", v)} />
              <Num label="Comp-off Request Window (days)" sm={4} disabled={!isEditable} value={form.compOffRequestWindowDays} set={(v) => setField("compOffRequestWindowDays", v)} />
            </Grid>

            <Divider sx={{ my: 3, borderColor: "#EEF2F6" }} />

            {/* Approval Workflow */}
            <SectionTitle>Approval Workflow</SectionTitle>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    sx={{ color: "#94A3B8", "&.Mui-checked": { color: TEAL } }}
                    checked={form.managerApprovalEnabled}
                    onChange={(e) => setField("managerApprovalEnabled", e.target.checked)}
                    disabled={!isEditable}
                  />
                }
                label={<Typography sx={{ fontSize: 14, color: TEXT_STRONG }}>Allow Managers to approve</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    sx={{ color: "#94A3B8", "&.Mui-checked": { color: TEAL } }}
                    checked={form.timesheetAutoApprove}
                    onChange={(e) => setField("timesheetAutoApprove", e.target.checked)}
                    disabled={!isEditable}
                  />
                }
                label={<Typography sx={{ fontSize: 14, color: TEXT_STRONG }}>Auto-approve clean timesheets</Typography>}
              />
            </Box>

            <Divider sx={{ my: 3, borderColor: "#EEF2F6" }} />

            {/* Retention */}
            <SectionTitle>Retention (days)</SectionTitle>
            <Grid container spacing={2}>
              <Num label="Screenshots" disabled={!isEditable} value={form.retention.screenshots} set={(v) => setNested("retention", "screenshots", v)} />
              <Num label="Time Entries" disabled={!isEditable} value={form.retention.timeEntries} set={(v) => setNested("retention", "timeEntries", v)} />
              <Num label="Attendance" disabled={!isEditable} value={form.retention.attendance} set={(v) => setNested("retention", "attendance", v)} />
              <Num label="Audit Log" disabled={!isEditable} value={form.retention.auditLog} set={(v) => setNested("retention", "auditLog", v)} />
            </Grid>

            {/* Footer — only shown when user has edit permission */}
            {isEditable && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  sx={{
                    textTransform: "capitalize",
                    fontWeight: 700,
                    fontSize: 14,
                    borderRadius: "8px",
                    px: 3.5,
                    py: 1,
                    color: "#fff",
                    boxShadow: "none",
                    background: TEAL_GRAD,
                    "&:hover": { background: TEAL_GRAD_HOVER, boxShadow: "0 4px 12px rgba(0,128,128,0.3)" },
                    "&.Mui-disabled": { background: "#CBD5E1", color: "#fff" },
                  }}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};

const TimeLoggerOrgs = () => {
  const { canEdit } = usePermissions();
  const isEditable = canEdit("timeLoggerOrgs");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const res = await getTimeLoggerOrgs();
      setRows(res?.data || []);
    } catch (err) {
      console.error("Failed to fetch TimeLogger orgs:", err);
      ToastError(err?.message || "Failed to load TimeLogger organisations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Update the entry in-place after a successful save
  const handleSaved = (updatedOrg) => {
    if (!updatedOrg) return;
    setRows((prev) =>
      prev.map((r) =>
        r.org && String(r.org._id) === String(updatedOrg._id) ? { ...r, org: updatedOrg } : r
      )
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress sx={{ color: TEAL }} />
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 10,
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,128,128,0.08)",
          }}
        >
          <BusinessIcon sx={{ fontSize: 36, color: TEAL }} />
        </Box>
        <Typography sx={{ color: TEXT_STRONG, fontWeight: 600 }}>
          No LMT admins found in the TimeLogger database.
        </Typography>
        <Typography variant="caption" sx={{ color: TEXT_MUTED }}>
          Set <code>isLmtAdmin: true</code> on a TimeLogger user to see them here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 1 }}>
      {rows.map((entry, idx) => (
        <OrgCard key={entry.user._id || idx} entry={entry} onSaved={handleSaved} isEditable={isEditable} />
      ))}
    </Box>
  );
};

export default TimeLoggerOrgs;
